import { useCallback, useState } from 'react'
import { useSsoToken } from './useSsoToken'
import { ApiValidationError, createTimeEntry } from '../services/claimsApi'
import { getApiErrorMessage } from '../utils/errorMessages'
import { toUtcDateOnly } from '../utils/formatters'

export interface TimeEntryInput {
  // yyyy-mm-dd, as the native date input produces it.
  date: string
  timeWorked: number
  // Optional for the backend, which uses it to catch a labour code left over from a
  // different work package.
  workPackageId: string
  labourCodeId: string
  billingRoleId: string
  // The status the user picked, as an id from /api/time-entries/statuses. The form labels this
  // control "Billing Status" because MC1 does, but the value is a time entry status and must be
  // sent as `statusId`. `billingStatus` is a separate backend field that carries no status —
  // sending it there books the time and silently never submits it for approval.
  statusId: string
  description: string
}

export interface TimeEntrySaved {
  // What went wrong after the time was booked: a submission Replicon rejected, or one whose
  // outcome it never confirmed. The entry is saved either way, so these are shown alongside
  // the confirmation rather than treated as a failed save. Empty on a clean save — the
  // backend no longer passes back the timesheet's own standing messages.
  warnings: string[]
  // The status Replicon actually holds, in words — not the one that was asked for. Submission
  // is asynchronous and seldom settles before the response, so asking for Approved normally
  // reads "Submitting", and nothing reports the outcome afterwards. Null when the backend sent
  // no status, so the UI can say nothing rather than guess.
  statusName: string | null
  // The booked entry's id, so the user can quote it. Replicon names an entry with a URN
  // (urn:replicon-tenant:<tenant>:timeentry:<id>), and only its last segment means anything to a
  // person. Null when the backend sent no id, so the UI says nothing rather than guess.
  entryId: string | null
}

// The last segment of a Replicon URN — the part that identifies the entry within the tenant.
export function toEntryId(uri: string | null | undefined): string | null {
  const id = uri?.split(':').pop()?.trim()
  return id || null
}

/**
 * Books a time entry against the claim (MSD-355).
 *
 * `save` resolves to true once the time entry is booked.
 */
export function useCreateTimeEntry(claimNumber: string) {
  const { getToken } = useSsoToken()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<TimeEntrySaved | null>(null)

  const save = async (input: TimeEntryInput): Promise<boolean> => {
    setSaving(true)
    setError(null)
    setSaved(null)
    try {
      const token = await getToken()
      const result = await createTimeEntry(
        claimNumber,
        {
          date: toUtcDateOnly(input.date) as Date,
          timeWorked: input.timeWorked,
          workPackageId: input.workPackageId || null,
          labourCodeId: input.labourCodeId,
          billingRoleId: input.billingRoleId,
          // No billing status is collected: the form's "Billing Status" control carries the time
          // entry status, which goes in statusId below. Left null rather than reused, so a real
          // billing status can be added later without changing what submits.
          billingStatus: null,
          description: input.description,
          // Empty means save it and leave it; an Approved id also submits it for approval.
          statusId: input.statusId || null,
        },
        token
      )

      setSaved({
        warnings: result.warnings ?? [],
        statusName: result.statusName ?? null,
        // One entry is booked per save, so the first id is the one.
        entryId: toEntryId(result.timeEntryIds?.[0]),
      })
      return true
    } catch (err: unknown) {
      console.error('[CreateTimeEntry] Failed to save the time entry:', err)
      // A rejected entry reports why (labour code not on the claim, hours out of range);
      // retrying the same values can't help, so the backend's reason is what's shown.
      const message =
        err instanceof ApiValidationError
          ? err.messages.join(' ')
          : getApiErrorMessage(err, 'Failed to save the time entry. Please try again.')
      setError(message)
      return false
    } finally {
      setSaving(false)
    }
  }

  // Clears the confirmation so the next entry starts from a blank form. Stable, because the
  // tab's auto-dismiss timer depends on it.
  const reset = useCallback(() => {
    setError(null)
    setSaved(null)
  }, [])

  return { saving, error, saved, save, reset }
}
